import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { goApi } from '../../../services';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Typography,
    Paper,
    IconButton,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import AddScheduleHomeworks from '../../batches/batch-schedules/AddScheduleHomeworks';
import { SimpleForm, Toolbar } from 'react-admin';
import AddExerciseAutoComplete from '../../../common/inputs/AddExerciseAutoComplete';

const ScheduleContent = ({ type }: any) => {
    const { id } = useParams();
    if (!id) return null;
    const [scheduleContentData, setScheduleContentData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedHomework, setSelectedHomework] = useState<any>(null);
    const [formData, setFormData] = useState<any>({});

    const fetchScheduleContent = async () => {
        setLoading(true);
        try {
            const res = await goApi.get(`/batches/schedules/${id}/contents?type=${type}`);

            setScheduleContentData(res?.data?.items || []);
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchScheduleContent();
    }, [id, type]);

    const openDeleteModal = (homework: any) => {
        setSelectedHomework(homework);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setSelectedHomework(null);
    };

    const openCreateModal = () => {
        setCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        setCreateModalOpen(false);
        setFormData({});
    };

    const handleCreate = async (data?: any) => {
        try {
            const dataToUse = data || formData;
            const payload =
                type === 'pdf' ? { pdfId: dataToUse.pdfId } : { exerciseId: dataToUse.exerciseId };

            await goApi.post(`/batches/schedules/${id}/contents/${type}`, payload);

            closeCreateModal();
            fetchScheduleContent();
        } catch (err) {
            console.error('Error adding homework:', err);
        }
    };

    const handleDelete = async () => {
        if (!selectedHomework) return;

        try {
            await goApi.delete(`/batches/schedules/${id}/contents/${selectedHomework.id}/remove`);
            closeDeleteModal();
            fetchScheduleContent();
        } catch (err) {
            console.error('Error deleting homework:', err);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedScheduleContent = scheduleContentData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
    );

    return (
        <div className="w-full">
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={openCreateModal}
                >
                    Add {type === 'pdf' ? 'Homework' : 'Exercise'}
                </Button>
            </div>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <CircularProgress />
                </div>
            ) : scheduleContentData.length === 0 ? (
                <Typography
                    variant="body1"
                    color="text.secondary"
                    style={{ padding: '2rem', textAlign: 'center' }}
                >
                    No {type === 'pdf' ? 'homework' : 'exercise'} available
                </Typography>
            ) : (
                <TableContainer component={Paper} elevation={1}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>Title</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Created At</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedScheduleContent.map(content => (
                                <TableRow key={content.id} hover>
                                    <TableCell>
                                        {content.pdf?.title ||
                                            content?.exercise?.title ||
                                            'Untitled'}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-4 h-4 flex flex-col items-center rounded-full bg-amber-200">
                                                C
                                            </div>
                                            <div className="whitespace-nowrap">
                                                {format(
                                                    new Date(content?.createdAt),
                                                    'dd-MMM-yyyy hh:mm a',
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-4 h-4 flex flex-col items-center rounded-full bg-pink-200">
                                                U
                                            </div>
                                            <div className="whitespace-nowrap">
                                                {format(
                                                    new Date(content?.updatedAt),
                                                    'dd-MMM-yyyy hh:mm a',
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell align="right">
                                        <div className="flex flex-row gap-2 justify-end">
                                            {content?.pdf?.url && (
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() =>
                                                        window.open(content.pdf.url, '_blank')
                                                    }
                                                >
                                                    view
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => openDeleteModal(content)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={scheduleContentData?.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            )}

            <Dialog
                open={deleteModalOpen}
                onClose={closeDeleteModal}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Delete Homework</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete "
                        {selectedHomework?.pdf?.title || 'this homework'}"? This action cannot be
                        undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteModal} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="error" variant="contained" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={createModalOpen}
                onClose={closeCreateModal}
                aria-labelledby="create-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="create-dialog-title">
                    Add New {type === 'pdf' ? 'Homework' : 'Exercise'}
                </DialogTitle>
                <DialogContent>
                    <SimpleForm
                        toolbar={<Toolbar />}
                        onSubmit={data => {
                            handleCreate(data);
                        }}
                    >
                        {type === 'pdf' ? (
                            <AddScheduleHomeworks type={'Homeworks'} fullWidth source={'pdfId'} />
                        ) : (
                            <AddExerciseAutoComplete
                                fullWidth
                                label="Add Exercise"
                                source={'exerciseId'}
                            />
                        )}
                    </SimpleForm>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateModal} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ScheduleContent;
